import { main_url } from "./Common";

export const emptyInvoiceSettingForm = {
  id: null,
  c_name: "",
  trading_name: "",
  country: "",
  registration_number: "",
  tax_number: "",
  c_email: "",
  website: "",
  c_mobile: "",
  c_phone: "",
  c_address: "",
  b_name: "",
  b_account_name: "",
  abn: "",
  bsb: "",
  acc: "",
  status: 1,
  c_logo: "",
};

export const safeInvoiceValue = (value) =>
  value === null || value === undefined ? "" : value;

export const resolveInvoiceLogoUrl = (logoPath) => {
  if (!logoPath) return "";
  const value = String(logoPath).trim();
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `${main_url()}${value}`;
  return `${main_url()}/${value}`;
};

export const DEFAULT_INVOICE_BRAND_LOGO_PATH = "/assets/img/logo1.png";

export const DEFAULT_INVOICE_PREVIEW_LOGO = `${
  process.env.PUBLIC_URL || ""
}${DEFAULT_INVOICE_BRAND_LOGO_PATH}`;

export function resolveInvoicePreviewLogoUrl({
  invoiceSetting = null,
} = {}) {
  return (
    resolveInvoiceLogoUrl(invoiceSetting?.c_logo) ||
    DEFAULT_INVOICE_PREVIEW_LOGO
  );
}

/** Absolute logo URL for emailed invoice HTML (remote clients cannot load bundled assets). */
export function resolveInvoiceEmailBrandLogoUrl({
  invoiceSetting = null,
} = {}) {
  const custom = resolveInvoiceLogoUrl(invoiceSetting?.c_logo);
  if (custom) return custom;
  return `${main_url()}${DEFAULT_INVOICE_BRAND_LOGO_PATH}`;
}

export function getInvoicePreviewTaxModeLabel(taxType) {
  const normalized = String(taxType || "").toLowerCase();

  if (normalized === "inclusive") return "Tax inclusive";
  if (normalized === "exclusive") return "Tax exclusive";
  if (normalized === "mixed") return "Mixed tax";
  return "";
}

export const mapRowToInvoiceForm = (row = {}) => ({
  id: row.id ?? null,
  c_name: safeInvoiceValue(row.c_name),
  trading_name: safeInvoiceValue(row.trading_name),
  country: safeInvoiceValue(row.country),
  registration_number: safeInvoiceValue(row.registration_number),
  tax_number: safeInvoiceValue(row.tax_number),
  c_email: safeInvoiceValue(row.c_email),
  website: safeInvoiceValue(row.website),
  c_mobile: safeInvoiceValue(row.c_mobile),
  c_phone: safeInvoiceValue(row.c_phone),
  c_address: safeInvoiceValue(row.c_address),
  b_name: safeInvoiceValue(row.b_name),
  b_account_name: safeInvoiceValue(row.b_account_name),
  abn: safeInvoiceValue(row.abn),
  bsb: safeInvoiceValue(row.bsb),
  acc: safeInvoiceValue(row.acc),
  status: Number(row.status || 0),
  c_logo: safeInvoiceValue(row.c_logo),
});

export const buildInvoiceSettingPayload = (formData) => ({
  c_name: formData.c_name.trim(),
  trading_name: formData.trading_name.trim() || null,
  country: formData.country.trim() || null,
  registration_number: formData.registration_number.trim() || null,
  tax_number: formData.tax_number.trim() || null,
  c_email: formData.c_email.trim() || null,
  website: formData.website.trim() || null,
  c_mobile: formData.c_mobile.trim() || null,
  c_phone: formData.c_phone.trim() || null,
  c_address: formData.c_address.trim() || null,
  b_name: formData.b_name.trim() || null,
  b_account_name: formData.b_account_name.trim() || null,
  abn: formData.abn.trim() || null,
  bsb: formData.bsb.trim() || null,
  acc: formData.acc.trim() || null,
  status: Number(formData.status || 0),
});

export const normalizeInvoiceSettingsList = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload && typeof payload === "object") {
    return [payload];
  }

  return [];
};

export const isInvoiceSettingApiSuccess = (response) => {
  if (!response || response.status < 200 || response.status >= 300) {
    return false;
  }

  const { status, success } = response.data || {};

  if (status === false || success === false) {
    return false;
  }

  if (status === true || success === true || status === 1 || success === 1) {
    return true;
  }

  // Some endpoints return only message/data on success.
  return status == null && success == null;
};

export const getInvoiceSettingApiMessage = (response, fallback = "") =>
  response?.data?.message || response?.data?.error || fallback;

export const extractCreatedInvoiceSettingId = (response) => {
  const payload = response?.data?.data;

  if (payload == null) {
    return null;
  }

  if (typeof payload === "object" && !Array.isArray(payload)) {
    return payload.id ?? payload.invoice_setting_id ?? null;
  }

  return null;
};

export const findInvoiceSettingById = (response, recordId) => {
  const items = normalizeInvoiceSettingsList(response);
  return items.find((row) => String(row.id) === String(recordId)) || null;
};

export function getInvoiceSettingTradingName(setting) {
  if (!setting) return "";
  return String(setting.trading_name || setting.c_name || "").trim();
}

export function getInvoiceSettingLegalName(setting) {
  if (!setting) return "";
  const trading = String(setting.trading_name || "").trim();
  const company = String(setting.c_name || "").trim();
  if (trading && company && trading !== company) return company;
  return "";
}

export function getInvoiceSettingAddressLines(setting) {
  if (!setting) return [];

  const address = String(setting.c_address || "").trim();
  const country = String(setting.country || "").trim();
  const lines = [];

  if (address) {
    address.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (trimmed) lines.push(trimmed);
    });
  }

  if (
    country &&
    !lines.some((line) => line.toLowerCase() === country.toLowerCase())
  ) {
    lines.push(country);
  }

  return lines;
}

export function getInvoiceSettingPhoneLines(setting) {
  if (!setting) return [];

  const mobile = String(setting.c_mobile || "").trim();
  const phone = String(setting.c_phone || "").trim();
  const lines = [];

  if (mobile) lines.push(mobile);
  if (phone && phone !== mobile) lines.push(phone);

  return lines;
}

export function getInvoiceSettingEmail(setting) {
  if (!setting) return "";
  return String(setting.c_email || "").trim();
}

export function formatInvoiceSettingWebsite(website) {
  const value = String(website || "").trim();
  if (!value) return "";
  return value.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

export function normalizeInvoiceSettingRecord(response) {
  const payload = response?.data?.data;
  if (!payload || Array.isArray(payload)) return null;
  return mapRowToInvoiceForm(payload);
}
